import { useEffect, useState } from "react";
import "@/App.css";

/* ------------------------------------------------------------------
   biolinks.tushit.in — one-page biolink
   Design continuity with tushit.in (gold-on-black editorial aesthetic).
   ------------------------------------------------------------------ */

const LINKS = [
  {
    id: "instagram",
    label: "Instagram",
    handle: "@tushitthakur",
    href: "https://instagram.com/tushitthakur",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.336 3.608 1.311.975.975 1.249 2.242 1.311 3.608.058 1.266.069 1.646.069 4.85s-.011 3.584-.069 4.85c-.062 1.366-.336 2.633-1.311 3.608-.975.975-2.242 1.249-3.608 1.311-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.336-3.608-1.311-.975-.975-1.249-2.242-1.311-3.608C2.175 15.747 2.163 15.367 2.163 12s.012-3.584.07-4.85c.062-1.366.336-2.633 1.311-3.608.975-.975 2.242-1.249 3.608-1.311C8.416 2.175 8.796 2.163 12 2.163zm0 1.838c-3.155 0-3.523.012-4.766.069-1.026.046-1.583.218-1.953.362-.49.19-.84.418-1.207.785-.367.367-.595.717-.785 1.207-.144.37-.316.927-.362 1.953-.057 1.243-.07 1.611-.07 4.766 0 3.155.013 3.523.07 4.766.046 1.026.218 1.583.362 1.953.19.49.418.84.785 1.207.367.367.717.595 1.207.785.37.144.927.316 1.953.362 1.243.057 1.611.07 4.766.07 3.155 0 3.523-.013 4.766-.07 1.026-.046 1.583-.218 1.953-.362.49-.19.84-.418 1.207-.785.367-.367.595-.717.785-1.207.144-.37.316-.927.362-1.953.057-1.243.07-1.611.07-4.766 0-3.155-.013-3.523-.07-4.766-.046-1.026-.218-1.583-.362-1.953-.19-.49-.418-.84-.785-1.207-.367-.367-.717-.595-1.207-.785-.37-.144-.927-.316-1.953-.362C15.523 4.013 15.155 4 12 4zm0 3.063A4.937 4.937 0 1 1 7.063 12 4.943 4.943 0 0 1 12 7.063zm0 8.137A3.2 3.2 0 1 0 8.8 12 3.2 3.2 0 0 0 12 15.2zm6.406-8.349a1.153 1.153 0 1 1-1.153-1.153 1.154 1.154 0 0 1 1.153 1.153z" />
      </svg>
    ),
  },
  {
    id: "telegram",
    label: "Telegram",
    handle: "@tushitthakur",
    href: "https://t.me/tushitthakur",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21.5 3.5 2.6 10.96c-1.18.47-1.17 1.13-.21 1.42l4.85 1.51 11.22-7.08c.53-.32 1.02-.15.62.21l-9.09 8.21h-.001l.001.001-.335 5.001c.44 0 .63-.2.87-.44l2.09-2.03 4.34 3.21c.8.44 1.37.21 1.57-.74L22.97 5.1c.29-1.16-.42-1.68-1.47-1.6z" />
      </svg>
    ),
  },
  {
    id: "twitter",
    label: "Twitter",
    handle: "@tushitthakur15",
    href: "https://twitter.com/tushitthakur15",
    icon: (
      // Classic Twitter bird (NOT the X logo) — per user request
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M23.643 4.937c-.835.37-1.732.62-2.675.733a4.67 4.67 0 0 0 2.048-2.578 9.3 9.3 0 0 1-2.958 1.13 4.66 4.66 0 0 0-7.938 4.25 13.229 13.229 0 0 1-9.602-4.868 4.66 4.66 0 0 0 1.442 6.22A4.647 4.647 0 0 1 1.8 9.165v.058a4.66 4.66 0 0 0 3.737 4.567 4.69 4.69 0 0 1-2.104.08 4.66 4.66 0 0 0 4.352 3.234A9.348 9.348 0 0 1 0 19.054 13.187 13.187 0 0 0 7.144 21.15c8.57 0 13.255-7.098 13.255-13.254 0-.202-.005-.403-.014-.602a9.47 9.47 0 0 0 2.323-2.41z" />
      </svg>
    ),
  },
  {
    id: "email",
    label: "Email",
    handle: "hi@tushit.in",
    href: "mailto:hi@tushit.in",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zm9 8.236L3.74 7.41A1 1 0 0 0 3 8.39V18h18V8.39a1 1 0 0 0-.74-.98L12 13.236zM12 11l8.5-5h-17L12 11z" />
      </svg>
    ),
  },
];

/* IST live clock — segments equal to "15" turn gold (mirrors tushit.in) */
function useIST() {
  const [t, setT] = useState({ h: "--", m: "--", s: "--" });
  useEffect(() => {
    const tick = () => {
      try {
        const parts = new Intl.DateTimeFormat("en-GB", {
          timeZone: "Asia/Kolkata",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }).formatToParts(new Date());
        const get = (k) => parts.find((p) => p.type === k)?.value ?? "00";
        setT({ h: get("hour"), m: get("minute"), s: get("second") });
      } catch {
        /* noop */
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return t;
}

/* Loader safety-net so it never sticks if CSS anim is suppressed */
function useLoaderSafety() {
  useEffect(() => {
    const id = setTimeout(() => {
      const l = document.querySelector(".loader");
      if (l) {
        l.style.opacity = "0";
        l.style.visibility = "hidden";
      }
    }, 1200);
    return () => clearTimeout(id);
  }, []);
}

function App() {
  const ist = useIST();
  useLoaderSafety();
  const year = new Date().getFullYear();
  const lucky = ist.h === "15" && ist.m === "15" && ist.s === "15";

  return (
    <div className="App">
      <div className="page" data-testid="page-root">
        {/* Loader */}
        <div className="loader" data-testid="loader" aria-hidden="true">
          <span>15</span>
        </div>

        {/* Corner registration marks */}
        <span className="reg reg-tl" aria-hidden="true">+</span>
        <span className="reg reg-tr" aria-hidden="true">+</span>
        <span className="reg reg-bl" aria-hidden="true">+</span>
        <span className="reg reg-br" aria-hidden="true">+</span>

        {/* Folio watermark */}
        <div className="folio" aria-hidden="true" data-testid="bg-fifteen">
          <span>15</span>
        </div>

        {/* Vertical Hindi rail */}
        <span className="vrail" aria-hidden="true">तुषित ठाकुर</span>

        <div className="shell">
          {/* Top meta strip */}
          <header className="meta" data-testid="meta">
            <div className="meta-l">
              <span className="dot" aria-hidden="true" />
              <span className="mono" data-testid="meta-left">TT · 15</span>
            </div>
            <div className="meta-r">
              <span className="mono ist-time" data-testid="ist-clock">
                IST ·{" "}
                <span className={ist.h === "15" ? "g15" : ""} data-testid="ist-h">{ist.h}</span>
                :
                <span className={ist.m === "15" ? "g15" : ""} data-testid="ist-m">{ist.m}</span>
                :
                <span className={ist.s === "15" ? "g15" : ""} data-testid="ist-s">{ist.s}</span>
              </span>
              <span className="dot" aria-hidden="true" />
            </div>
          </header>

          {/* Hero */}
          <main className="hero" data-testid="hero">
            {/* Brandmark — italic outlined T (raised so only its bottom is
                covered by the photo's top) and a separate "15" sitting to the
                right of the photo. The photo is centred and unmoved.
                When IST hits 15:15:15 the T and 15 light up gold (mirrors the
                clock's own "15" highlighting). */}
            <div
              className={`brandmark${lucky ? " is-lucky" : ""}`}
              data-testid="brandmark"
              data-lucky={lucky ? "true" : "false"}
            >
              <span className="bm-T" aria-hidden="true">T</span>
              <span className="bm-15" aria-hidden="true">15</span>
              <div className="avatar" data-testid="avatar" aria-label="Tushit Thakur">
                <img
                  src="/profile.webp"
                  alt="Tushit Thakur"
                  width="200"
                  height="200"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>

            {/* Code-style golden URL */}
            <a
              className="url-link"
              href="https://biolinks.tushit.in/"
              data-testid="domain-link"
              aria-label="biolinks.tushit.in"
            >
              biolinks.tushit.in
            </a>

            {/* Link pills */}
            <nav className="links" data-testid="links" aria-label="Biolinks">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  target={l.id === "email" ? undefined : "_blank"}
                  rel={l.id === "email" ? undefined : "noopener noreferrer"}
                  className="link-btn"
                  data-testid={`link-${l.id}`}
                  aria-label={`${l.label} — ${l.handle}`}
                >
                  <span className="icon" aria-hidden="true">{l.icon}</span>
                  <span className="label">{l.label}</span>
                  <span className="handle">{l.handle}</span>
                </a>
              ))}
            </nav>
          </main>

          {/* Footer */}
          <footer className="foot" data-testid="footer" role="contentinfo">
            <span className="mono">© <span data-testid="footer-year">{year}</span></span>
            <span className="foot-rule" aria-hidden="true" />
            <span className="mono mid">Tushit Thakur</span>
            <span className="foot-rule" aria-hidden="true" />
            <span className="mono">Reserved</span>
          </footer>
        </div>
      </div>
    </div>
  );
}

export default App;
